import { TableToolbar, TableFilter, SortableHeader } from '../TableControls'
import { matchesTableSearch, sortTableRows, useTableSort } from '../../lib/tableControls'
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import {
  FolderKanban,
  Folder,
  FolderOpen,
  Plus,
  MoreHorizontal,
  Bot,
  Users,
  Pencil,
  Archive,
  Trash2,
} from "lucide-react";

import { supabase, getCurrentSession } from "../../lib/supabase";
import { dayLabel } from "../../lib/dayLabel";
import {
  daysAgoKey,
  fetchTeamAgentUsageDaily,
  toDateKey,
  type AgentUsageDailyRow,
} from "../../lib/agentUsage";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Checkbox } from "../ui/checkbox";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "../ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { buttonVariants } from "../ui/button";

// ============================================================================
// TeamManagement/ProjectsPanel.tsx
//
// Nested "Projects" tab content for a single pod, inside TeamManagement.tsx's
// selected-pod detail panel. Data model/writes are identical to the
// standalone Projects.tsx page (same `manage-projects` edge function, same
// `projects`/`project_pods`/agents_team_visible queries + the
// get_team_agent_usage_daily RPC via src/lib/agentUsage.ts for tokens/$) --
// this component just re-scopes the *display* to the projects assigned to
// one pod (via project_pods) and presents the detail as a Sheet instead of a
// second list+detail column, since it's already nested inside the pod's own
// detail panel (2026-09-29).
//
// Spend per project is grouped by the RPC's own project_id column (not an
// agent -> project join here): agents the caller can't see in detail come
// back with agent_id NULL but still carry project_id, so project totals are
// team-wide for every role.
//
// Mounted unconditionally (not gated on the "Projects" tab being active) so
// its badge count is known before the user ever clicks into the tab -- see
// the `active` prop, which only gates the rendered content, not the
// data-fetching effects.
// ============================================================================

interface Pod {
  id: string;
  name: string;
  description: string | null;
}

// Structural subset of TeamManagement.tsx's own CombinedUser -- that's the
// shape `members` is actually passed as (see TeamManagement.tsx's loadAll,
// which already fetches every team member via get-all-team-users). Kept as
// a separate, narrower type here rather than importing CombinedUser so this
// panel doesn't depend on TeamManagement's full user-management shape, just
// the fields the Members column in the table below actually needs.
export interface ProjectPanelMember {
  id: string;
  auth_user_id: string | null;
  user_name: string | null;
  user_email: string | null;
  avatar_url: string | null;
  pod_id: string | null;
}

interface ProjectsPanelProps {
  teamId: string;
  pod: Pod;
  pods: Pod[];
  isAdmin: boolean;
  active: boolean;
  onCountChange?: (podId: string, count: number) => void;
  createRequest?: number;
  // Every team member (not just this pod's) -- used to resolve a
  // project_members row (auth_user_id) to a display name/avatar for the
  // Members column below. NOT filtered by pod_id anymore: see the
  // membersByProject comment -- membership is now the direct
  // project_members roster (20261005000000_add_project_members.sql), not
  // derived from who happens to share a pod with the project.
  members: ProjectPanelMember[];
}

interface Project {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  archived_at: string | null;
}

interface AgentRow {
  id: string;
  name: string;
  project_id: string | null;
}

function initialsOf(name: string): string {
  return (
    name
      .split(/[\s-]+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?"
  );
}

function memberDisplayName(
  m: Pick<ProjectPanelMember, "user_name" | "user_email">,
): string {
  return m.user_name || m.user_email?.split("@")[0] || "Unknown user";
}

// Exact same hash-based palette as Projects.tsx -- kept in sync deliberately
// so a given project renders the same avatar color whether viewed from the
// standalone Projects page or from here.
const PROJECT_AVATAR_PALETTE = [
  "bg-pink-500",
  "bg-blue-500",
  "bg-emerald-600",
  "bg-violet-600",
  "bg-amber-500",
  "bg-cyan-600",
];

function paletteFor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++)
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return PROJECT_AVATAR_PALETTE[hash % PROJECT_AVATAR_PALETTE.length];
}

export const ProjectsPanel: React.FC<ProjectsPanelProps> = ({
  teamId,
  pod,
  pods,
  isAdmin,
  active,
  onCountChange,
  createRequest = 0,
  members,
}) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const { sort, toggleSort } = useTableSort('name');
  const [projectPodIds, setProjectPodIds] = useState<Map<string, Set<string>>>(
    new Map(),
  );
  const [projectMemberIds, setProjectMemberIds] = useState<
    Map<string, Set<string>>
  >(new Map());
  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [usageRows, setUsageRows] = useState<AgentUsageDailyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [creating, setCreating] = useState(false);

  const [detailProjectId, setDetailProjectId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editMemberIds, setEditMemberIds] = useState<Set<string>>(new Set());
  const [savingEdit, setSavingEdit] = useState(false);

  const [togglingPodId, setTogglingPodId] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (createRequest > 0) {
      setCreateName("");
      setCreateDescription("");
      setCreateOpen(true);
    }
  }, [createRequest]);

  // Data-fetching runs regardless of `active` -- keyed on [teamId, pod.id] --
  // so the "Projects" tab badge count is correct even before the tab is
  // clicked (see this file's header comment).
  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [projectsRes, projectPodsRes, projectMembersRes, agentsRes] =
        await Promise.all([
          (supabase.from as any)("projects")
            .select("id, name, description, created_at, archived_at")
            .eq("team_id", teamId)
            .is("deleted_at", null)
            .order("created_at", { ascending: false }),
          (supabase.from as any)("project_pods").select("project_id, pod_id"),
          (supabase.from as any)("project_members").select(
            "project_id, auth_user_id",
          ),
          (supabase.from as any)("agents_team_visible")
            .select("id, name, project_id")
            .eq("team_id", teamId),
        ]);

      if (projectsRes.error) throw projectsRes.error;
      if (projectPodsRes.error) throw projectPodsRes.error;
      if (projectMembersRes.error) throw projectMembersRes.error;
      if (agentsRes.error) throw agentsRes.error;

      setProjects((projectsRes.data ?? []) as Project[]);

      const podMap = new Map<string, Set<string>>();
      for (const row of (projectPodsRes.data ?? []) as {
        project_id: string;
        pod_id: string;
      }[]) {
        if (!podMap.has(row.project_id)) podMap.set(row.project_id, new Set());
        podMap.get(row.project_id)!.add(row.pod_id);
      }
      setProjectPodIds(podMap);

      const memberIdMap = new Map<string, Set<string>>();
      for (const row of (projectMembersRes.data ?? []) as {
        project_id: string;
        auth_user_id: string;
      }[]) {
        if (!memberIdMap.has(row.project_id))
          memberIdMap.set(row.project_id, new Set());
        memberIdMap.get(row.project_id)!.add(row.auth_user_id);
      }
      setProjectMemberIds(memberIdMap);

      const agentRows = (agentsRes.data ?? []) as AgentRow[];
      setAgents(agentRows);

      // Trailing 7 days, server-priced usage (see src/lib/agentUsage.ts).
      // Team-wide aggregate RPC, same exposure as TopProjects. No agent
      // filter: null-agent rows must be included in project totals.
      setUsageRows(
        await fetchTeamAgentUsageDaily(
          teamId,
          daysAgoKey(6),
          toDateKey(new Date()),
        ),
      );
    } catch (err) {
      console.error("Failed to load projects for pod:", err);
      setError("Could not load projects for this team.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [teamId, pod.id]);

  // Projects assigned to THIS pod specifically, via project_pods -- not the
  // team's full project list (that's `projects` above).
  const podProjects = useMemo(
    () => projects.filter((p) => projectPodIds.get(p.id)?.has(pod.id)),
    [projects, projectPodIds, pod.id],
  );

  useEffect(() => {
    onCountChange?.(pod.id, podProjects.length);
  }, [pod.id, podProjects.length]);

  const agentCountByProject = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of agents) {
      if (!a.project_id) continue;
      counts.set(a.project_id, (counts.get(a.project_id) ?? 0) + 1);
    }
    return counts;
  }, [agents]);

  // Keyed by project id (unassigned usage lands under UNASSIGNED_PROJECT_KEY,
  // which never matches a real project here).
  // Table "Members" column: the direct project_members roster
  // (20261005000000_add_project_members.sql) -- who's actually on the
  // project, resolved to display info via the `members` prop. Deliberately
  // NOT derived from project_pods/pod_id anymore: that union over-counted
  // the moment one pod worked on more than one project (every pod member
  // would show on every one of the pod's projects, whether they personally
  // touched it or not).
  //
  // NOT read-only: this panel's own "Manage Project" edit sheet
  // (editMemberIds, handleSaveEdit below) also adds/removes project_members,
  // independently of the per-member toggle on the standalone Projects.tsx
  // page. Both call the same manage-projects add_member/remove_member
  // actions with the same team_id scoping, so neither can revert the
  // other's change out from under it -- but they are two separate editing
  // surfaces for the same roster, not one owner, so keep that in mind if
  // you're looking for "the" place membership gets edited.
  const membersByProject = useMemo(() => {
    const map = new Map<string, ProjectPanelMember[]>();
    for (const project of projects) {
      const memberIds = projectMemberIds.get(project.id);
      map.set(
        project.id,
        !memberIds || memberIds.size === 0
          ? []
          : members.filter(
              (m) => m.auth_user_id && memberIds.has(m.auth_user_id),
            ),
      );
    }
    return map;
  }, [projects, projectMemberIds, members]);

  // Table "Last Updated" column: there's no `updated_at` on `projects`, so
  // this is the most recent day with usage attributed to the project within
  // the trailing-7-day window already loaded above -- real signal, not a
  // fabricated timestamp. Falls back to `created_at` (relative) for a
  // project with no usage in that window, which reads as "created N ago"
  // rather than claiming a false recent update.
  const lastUsageDateByProject = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of usageRows) {
      if (!r.projectId) continue;
      const prev = map.get(r.projectId);
      if (!prev || r.usageDate > prev) map.set(r.projectId, r.usageDate);
    }
    return map;
  }, [usageRows]);

  const detailProject = projects.find((p) => p.id === detailProjectId) ?? null;
  const detailAgents = useMemo(
    () =>
      detailProject
        ? agents.filter((a) => a.project_id === detailProject.id)
        : [],
    [agents, detailProject],
  );
  const detailAssignedPodIds = detailProject
    ? (projectPodIds.get(detailProject.id) ?? new Set<string>())
    : new Set<string>();

  const openCreate = () => {
    setCreateName("");
    setCreateDescription("");
    setCreateOpen(true);
  };

  const openDetail = (project: Project) => {
    setDetailProjectId(project.id);
    setEditName(project.name);
    setEditDescription(project.description ?? "");
    setEditMemberIds(new Set(projectMemberIds.get(project.id) ?? []));
    setDetailOpen(true);
  };

  const handleCreateProject = async () => {
    if (!createName.trim()) return;
    setCreating(true);
    try {
      const session = await getCurrentSession();
      if (!session) throw new Error("No session found");

      const { data, error: fnError } = await supabase.functions.invoke(
        "manage-projects",
        {
          body: {
            action: "create",
            team_id: teamId,
            name: createName.trim(),
            description: createDescription.trim() || null,
          },
          headers: { Authorization: `Bearer ${session.access_token}` },
        },
      );
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      const newProjectId = data?.project?.id;
      // Chain the assign_pod call so the newly created project shows up in
      // this pod's list immediately, rather than being left unassigned --
      // see this file's header comment / task spec.
      if (newProjectId) {
        const { error: assignError, data: assignData } =
          await supabase.functions.invoke("manage-projects", {
            body: {
              action: "assign_pod",
              team_id: teamId,
              project_id: newProjectId,
              pod_id: pod.id,
            },
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
        if (assignError) throw assignError;
        if (assignData?.error) throw new Error(assignData.error);
      }

      toast.success("Project created");
      setCreateOpen(false);
      await loadAll();
    } catch (err: any) {
      console.error("Failed to create project:", err);
      toast.error(err.message || "Failed to create project");
    } finally {
      setCreating(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!detailProject || !editName.trim()) return;
    setSavingEdit(true);
    try {
      const session = await getCurrentSession();
      if (!session) throw new Error("No session found");
      const { data, error: fnError } = await supabase.functions.invoke(
        "manage-projects",
        {
          body: {
            action: "update",
            team_id: teamId,
            project_id: detailProject.id,
            name: editName.trim(),
            description: editDescription.trim() || null,
          },
          headers: { Authorization: `Bearer ${session.access_token}` },
        },
      );
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      const currentMemberIds =
        projectMemberIds.get(detailProject.id) ?? new Set<string>();
      const memberChanges = [
        ...[...editMemberIds]
          .filter((id) => !currentMemberIds.has(id))
          .map((member_auth_user_id) => ({
            action: "add_member" as const,
            member_auth_user_id,
          })),
        ...[...currentMemberIds]
          .filter((id) => !editMemberIds.has(id))
          .map((member_auth_user_id) => ({
            action: "remove_member" as const,
            member_auth_user_id,
          })),
      ];

      await Promise.all(
        memberChanges.map(async ({ action, member_auth_user_id }) => {
          const { data: memberData, error: memberError } =
            await supabase.functions.invoke("manage-projects", {
              body: {
                action,
                team_id: teamId,
                project_id: detailProject.id,
                member_auth_user_id,
              },
              headers: { Authorization: `Bearer ${session.access_token}` },
            });
          if (memberError) throw memberError;
          if (memberData?.error) throw new Error(memberData.error);
        }),
      );

      toast.success("Changes saved successfully", {
        description: "Project details and people assignments were updated.",
        className:
          "border-l-4 border-l-pink-500 rounded-none shadow-[0_6px_18px_rgba(15,23,42,0.14)]",
      });
      setDetailOpen(false);
      await loadAll();
    } catch (err: any) {
      toast.error(err.message || "Failed to update project");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const session = await getCurrentSession();
      if (!session) throw new Error("No session found");
      const { error: fnError } = await supabase.functions.invoke(
        "manage-projects",
        {
          body: {
            action: "delete",
            team_id: teamId,
            project_id: deleteTarget.id,
          },
          headers: { Authorization: `Bearer ${session.access_token}` },
        },
      );
      if (fnError) throw fnError;
      toast.success("Project deleted");
      setDeleteTarget(null);
      setDetailOpen(false);
      setDetailProjectId(null);
      await loadAll();
    } catch (err: any) {
      toast.error(err.message || "Failed to delete project");
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleArchive = async (project: Project) => {
    try {
      const session = await getCurrentSession();
      if (!session) throw new Error("No session found");
      const { data, error: fnError } = await supabase.functions.invoke(
        "manage-projects",
        {
          body: {
            action: project.archived_at ? "unarchive" : "archive",
            team_id: teamId,
            project_id: project.id,
          },
          headers: { Authorization: `Bearer ${session.access_token}` },
        },
      );
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);
      toast.success(
        project.archived_at ? "Project unarchived" : "Project archived",
      );
      await loadAll();
    } catch (err: any) {
      toast.error(err.message || "Failed to update project archive status");
    }
  };

  const handleTogglePod = async (targetPod: Pod, checked: boolean) => {
    if (!detailProject) return;
    setTogglingPodId(targetPod.id);
    try {
      const session = await getCurrentSession();
      if (!session) throw new Error("No session found");
      const { error: fnError } = await supabase.functions.invoke(
        "manage-projects",
        {
          body: {
            action: checked ? "assign_pod" : "unassign_pod",
            team_id: teamId,
            project_id: detailProject.id,
            pod_id: targetPod.id,
          },
          headers: { Authorization: `Bearer ${session.access_token}` },
        },
      );
      if (fnError) throw fnError;
      await loadAll();
    } catch (err: any) {
      toast.error(err.message || "Failed to update team assignment");
    } finally {
      setTogglingPodId(null);
    }
  };

  const visibleProjects = sortTableRows(podProjects.filter(project => matchesTableSearch(query, project.name, project.description)
    && (statusFilter === 'all' || (statusFilter === 'archived' ? !!project.archived_at : !project.archived_at))), sort, {
      name: project => project.name, members: project => membersByProject.get(project.id)?.length || 0,
      updated: project => Date.parse(lastUsageDateByProject.get(project.id) || project.created_at),
      status: project => project.archived_at ? 'Archived' : 'Active',
    });

  return (
    <div className={active ? "" : "hidden"}>
      {active && !loading && <TableToolbar label="team projects" query={query} onQueryChange={setQuery} count={visibleProjects.length} total={podProjects.length}
        active={!!query || statusFilter !== 'all'} onClear={() => { setQuery(''); setStatusFilter('all') }}>
        <TableFilter label="Filter team project status" value={statusFilter} onChange={setStatusFilter} allLabel="All statuses" options={[{ value: 'active', label: 'Active' }, { value: 'archived', label: 'Archived' }]} />
      </TableToolbar>}
      {active && podProjects.length > 0 && visibleProjects.length === 0 && <p className="py-4 text-sm text-muted-foreground">No projects match these filters.</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}

      {!error && (
        <>
          {podProjects.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
                <FolderKanban className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="font-bold text-foreground">
                Start your first project
              </p>
              <p className="max-w-sm text-sm text-muted-foreground">
                Projects group the standups and work items your {pod.name} team
                is tracking.
              </p>
              {isAdmin && (
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={openCreate}
                >
                  <Plus className="h-4 w-4" />
                  Create Project
                </Button>
              )}
            </div>
          ) : (
            // Full-bleed table, matching docs/UI_STYLE_GUIDE.md §3/§4/§6 (row
            // dividers reaching the panel edge, no card radius) -- same
            // pattern as the Members table in TeamManagement.tsx itself, one
            // level up. min-w- + overflow-x-auto on the wrapper, never the
            // page body, per CLAUDE.md's responsive golden rule.
            <div className="-mx-4 overflow-x-auto sm:-mx-6">
              <table className="w-full min-w-[760px] caption-bottom text-sm">
                <thead className="[&_tr]:border-b">
                  <tr className="border-b">
                    <SortableHeader className="h-11 px-4 text-left align-middle font-medium text-muted-foreground" column="name" label="Project" sort={sort} onSort={toggleSort} />
                    <SortableHeader className="h-11 px-4 text-left align-middle font-medium text-muted-foreground" column="members" label="Members" sort={sort} onSort={toggleSort} />
                    <th className="h-11 px-4 text-left align-middle font-medium text-muted-foreground">
                      Standups
                    </th>
                    <SortableHeader className="h-11 px-4 text-left align-middle font-medium text-muted-foreground" column="updated" label="Last Updated" sort={sort} onSort={toggleSort} />
                    <SortableHeader className="h-11 px-4 text-left align-middle font-medium text-muted-foreground" column="status" label="Status" sort={sort} onSort={toggleSort} />
                    {isAdmin && <th className="h-11 px-4" />}
                  </tr>
                </thead>
                <tbody>
                  {visibleProjects.map((project) => {
                    const projMembers = membersByProject.get(project.id) ?? [];
                    const shownMembers = projMembers.slice(0, 3);
                    const extraCount = projMembers.length - shownMembers.length;
                    const lastUsageDate = lastUsageDateByProject.get(
                      project.id,
                    );
                    const agentCount = agentCountByProject.get(project.id) ?? 0;
                    return (
                      <tr
                        key={project.id}
                        className={`border-b hover:bg-muted/50 ${project.archived_at ? "opacity-60" : ""}`}
                      >
                        <td className="p-4 align-middle">
                          <button
                            type="button"
                            onClick={() => openDetail(project)}
                            className="flex min-h-[44px] items-center gap-2.5 text-left"
                          >
                            <Folder className="h-5 w-5 shrink-0 fill-amber-400 text-amber-500" />
                            <div className="min-w-0">
                              <div className="truncate font-medium text-foreground">
                                {project.name}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {agentCount} agent{agentCount === 1 ? "" : "s"}
                              </div>
                            </div>
                          </button>
                        </td>
                        <td className="p-4 align-middle">
                          {projMembers.length === 0 ? (
                            <span className="text-xs text-muted-foreground">
                              No members
                            </span>
                          ) : (
                            <div className="flex items-center">
                              <div className="flex -space-x-2">
                                {shownMembers.map((m) => (
                                  <Avatar
                                    key={m.id}
                                    className="h-7 w-7 border-2 border-background"
                                  >
                                    {m.avatar_url && (
                                      <AvatarImage src={m.avatar_url} alt="" />
                                    )}
                                    <AvatarFallback
                                      className={`text-[10px] font-semibold text-white ${paletteFor(m.id)}`}
                                    >
                                      {initialsOf(memberDisplayName(m))}
                                    </AvatarFallback>
                                  </Avatar>
                                ))}
                              </div>
                              {extraCount > 0 && (
                                <span className="ml-2 text-xs text-muted-foreground">
                                  +{extraCount} more
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        {/* No standups-per-project data exists yet (standups
                            aren't tied to a project anywhere in the schema) --
                            an em dash rather than a fabricated count. */}
                        <td className="p-4 align-middle text-muted-foreground">
                          –
                        </td>
                        <td className="p-4 align-middle text-muted-foreground">
                          {lastUsageDate
                            ? dayLabel(lastUsageDate)
                            : formatDistanceToNow(
                                new Date(project.created_at),
                                { addSuffix: true },
                              )}
                        </td>
                        <td className="p-4 align-middle">
                          <span
                            className={
                              project.archived_at
                                ? "inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700"
                                : "inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700"
                            }
                          >
                            <span
                              className={
                                project.archived_at
                                  ? "h-1.5 w-1.5 rounded-full bg-slate-500"
                                  : "h-1.5 w-1.5 rounded-full bg-emerald-500"
                              }
                            />
                            {project.archived_at ? "Archived" : "Active"}
                          </span>
                        </td>
                        {isAdmin && (
                          <td className="p-4 align-middle text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label={`More actions for ${project.name}`}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() =>
                                    navigate(
                                      `/dashboard/team/${teamId}/projects/${project.id}`,
                                    )
                                  }
                                >
                                  <FolderOpen className="mr-2 h-4 w-4" />
                                  Open Project
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => openDetail(project)}
                                >
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Manage Project
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleToggleArchive(project)}
                                >
                                  <Archive className="mr-2 h-4 w-4" />
                                  {project.archived_at
                                    ? "Unarchive"
                                    : "Archive"}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                                  onClick={() => setDeleteTarget(project)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete project
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>New project</SheetTitle>
          </SheetHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Project name
              </label>
              <Input
                placeholder="e.g, Marketing"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Description
              </label>
              <textarea
                className="min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Optional"
                value={createDescription}
                onChange={(e) => setCreateDescription(e.target.value)}
              />
            </div>
          </div>
          <SheetFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!createName.trim() || creating}
              onClick={handleCreateProject}
            >
              <Plus className="mr-1.5 h-4 w-4" />
              {creating ? "Creating…" : "Create Project"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="flex h-full w-full flex-col gap-0 p-0 sm:max-w-lg">
          <SheetHeader className="border-b px-8 py-6">
            <SheetTitle>Manage Project</SheetTitle>
          </SheetHeader>
          {detailProject && (
            <div className="min-h-0 flex-1 overflow-y-auto px-8 py-6">
              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">
                    Team
                  </label>
                  <div className="flex h-12 items-center gap-3 rounded-md border border-input bg-background px-4 text-muted-foreground">
                    <Users className="h-5 w-5" />
                    <span>{pod.name}</span>
                    <span className="ml-auto text-xs">Current team</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">
                    Project name
                  </label>
                  <div className="relative">
                    <Input
                      value={editName}
                      maxLength={50}
                      onChange={(e) => setEditName(e.target.value)}
                      disabled={!isAdmin}
                      className="h-12 pr-14"
                    />
                    <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                      {editName.length}/50
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-foreground">
                      Add people and connectors
                    </label>
                    {isAdmin && (
                      <button
                        type="button"
                        className="text-sm font-medium text-primary hover:underline"
                        onClick={() => {
                          const ids = members
                            .filter((member) => member.auth_user_id)
                            .map((member) => member.auth_user_id!);
                          setEditMemberIds((current) =>
                            current.size === ids.length
                              ? new Set()
                              : new Set(ids),
                          );
                        }}
                      >
                        {editMemberIds.size ===
                        members.filter((member) => member.auth_user_id).length
                          ? "Clear all"
                          : "Select all"}
                      </button>
                    )}
                  </div>

                  <div className="overflow-hidden rounded-md border border-input bg-background">
                    {members
                      .filter((member) => member.auth_user_id)
                      .map((member, index, list) => {
                        const memberId = member.auth_user_id!;
                        const name = memberDisplayName(member);
                        return (
                          <label
                            key={memberId}
                            className={`flex min-h-[72px] items-center gap-3 px-4 py-3 ${
                              index < list.length - 1
                                ? "border-b border-border"
                                : ""
                            } ${isAdmin ? "cursor-pointer hover:bg-muted/40" : ""}`}
                          >
                            <Checkbox
                              checked={editMemberIds.has(memberId)}
                              disabled={!isAdmin || savingEdit}
                              onCheckedChange={(checked) => {
                                setEditMemberIds((current) => {
                                  const next = new Set(current);
                                  if (checked === true) next.add(memberId);
                                  else next.delete(memberId);
                                  return next;
                                });
                              }}
                            />
                            <Avatar className="h-9 w-9 shrink-0">
                              {member.avatar_url && (
                                <AvatarImage src={member.avatar_url} alt="" />
                              )}
                              <AvatarFallback
                                className={`text-xs font-semibold text-white ${paletteFor(memberId)}`}
                              >
                                {initialsOf(name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-medium text-foreground">
                                {name}
                              </div>
                              <div className="truncate text-sm text-muted-foreground">
                                {member.user_email || "Team member"}
                              </div>
                            </div>
                            <span className="text-sm text-muted-foreground">
                              Human
                            </span>
                          </label>
                        );
                      })}

                    {detailAgents.map((agent, index) => (
                      <div
                        key={agent.id}
                        className={`flex min-h-[72px] items-center gap-3 px-4 py-3 ${
                          index < detailAgents.length - 1
                            ? "border-b border-border"
                            : ""
                        }`}
                      >
                        <div className="h-4 w-4 shrink-0" aria-hidden="true" />
                        <Avatar className="h-9 w-9 shrink-0">
                          <AvatarFallback
                            className={`text-xs font-semibold text-white ${paletteFor(agent.id)}`}
                          >
                            {initialsOf(agent.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-foreground">
                            {agent.name}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Assigned connector
                          </div>
                        </div>
                        <span className="text-sm text-muted-foreground">
                          Connector
                        </span>
                      </div>
                    ))}

                    {members.filter((member) => member.auth_user_id).length ===
                      0 &&
                      detailAgents.length === 0 && (
                        <p className="px-4 py-6 text-sm text-muted-foreground">
                          No people or connectors are available yet.
                        </p>
                      )}
                  </div>
                </div>

                <div className="space-y-2 border-t border-border pt-5">
                  <div className="text-sm font-medium text-foreground">
                    Teams on this project
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Visibility/reporting tags for this project.
                  </p>
                  {pods.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No teams set up yet.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                      {pods.map((p) => (
                        <label
                          key={p.id}
                          className={`flex min-h-[44px] items-center gap-2 rounded-md border border-border p-2 ${isAdmin ? "cursor-pointer hover:bg-muted/50" : ""}`}
                        >
                          <Checkbox
                            checked={detailAssignedPodIds.has(p.id)}
                            disabled={!isAdmin || togglingPodId === p.id}
                            onCheckedChange={(checked) =>
                              handleTogglePod(p, checked === true)
                            }
                          />
                          <span className="text-sm">{p.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {isAdmin && detailProject && (
            <SheetFooter className="border-t px-8 py-5 sm:justify-between">
              <Button
                variant="outline"
                onClick={() => setDetailOpen(false)}
                disabled={savingEdit}
              >
                Cancel
              </Button>
              <Button
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                disabled={!editName.trim() || savingEdit}
                onClick={handleSaveEdit}
              >
                {savingEdit ? "Saving…" : "Save Changes"}
              </Button>
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Connectors pinned to it will become unassigned. This can&apos;t be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: "destructive" })}
              disabled={deleting}
              onClick={(e) => {
                e.preventDefault();
                handleConfirmDelete();
              }}
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProjectsPanel;
